import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { addPreset } from 'stores/sensor/controllersSlice';
import { useDispatch, useSelector } from 'react-redux';
import CustomSnackbar from 'components/CustomSnackbar';
import SaveIcon from '@mui/icons-material/Save';
import { LOCK_LEVELS } from '../../../../constants/lockOptions';

const PALETTE = [
  '#4CAF50',
  '#03A9F4',
  '#FF9800',
  '#E91E63',
  '#9C27B0',
  '#009688',
  '#795548',
  '#607D8B',
  '#3F51B5',
  '#F44336',
];

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      mt: '-5px',
    },
  },
};

export default function PresetFormSimple({ onSaved }) {
  const currentMeasurements = useSelector(state => state.controllers.currentMeasurements);
  const isRelayModel = ['RELAY_2CH', 'RELAY_2CH_NC', 'RELAY_4CH', 'RELAY_4CH_NC'].includes(currentMeasurements?.controller_model);
  const deviceType = isRelayModel ? 'Controller-relay-SEN54' : currentMeasurements?.device_type;
  const [controlMode, setControlMode] = useState('solai');
  const [comfortMode, setComfortMode] = useState('Neutral');
  const [channel1, setChannel1] = useState('off');
  const [channel2, setChannel2] = useState('off');
  const [smartMode, setSmartMode] = useState(false);
  const [power, setPower] = useState('on');
  const [mode, setMode] = useState('auto');
  const [temp, setTemp] = useState(22);
  const [fan, setFan] = useState('auto');
  const [lock, setLock] = useState(0);
  const [name, setName] = useState('');
  const allPresets = useSelector(state => state.controllers.hourlyPresetsArray || []);
  const presets = useMemo(
    () => allPresets.filter(preset => !preset.device_type || preset.device_type === deviceType),
    [allPresets, deviceType],
  );
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [msg, setMsg] = useState('');
  const dispatch = useDispatch();

  // NEW: collapse state
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen(o => !o);

  const nextColor = useMemo(() => {
    const used = new Set(presets.map(p => p.color).filter(Boolean));
    return PALETTE.find(c => !used.has(c)) || PALETTE[presets.length % PALETTE.length];
  }, [presets]);
  const [selectedColor, setSelectedColor] = useState(nextColor);

  const TEMPS = Array.from({ length: 30 - 15 + 1 }, (_, i) => 15 + i);
  const relayMode = mode === 'heat' ? 'heat' : 'cool';
  const relayPower = channel1 === 'on' && channel2 === 'on' ? 'on' : 'off';

  const handleRelayPowerChange = value => {
    setChannel1(value);
    setChannel2(value);
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setSnack({ open: true, message: 'Please enter a preset name', severity: 'error' });
      return;
    }
    if (presets.length > 9) {
      setSnack({
        open: true,
        message: 'You already have 10 presets, delete one to add a new',
        severity: 'error',
      });
      return;
    }

    const id =
      globalThis.crypto && crypto.randomUUID
        ? crypto.randomUUID()
        : `p_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    const normalizedMode = isRelayModel ? relayMode : mode === 'fan' ? 'fan only' : mode;
    const relayPresetValues =
      controlMode === 'manual'
        ? {
            smart_mode: false,
            comfort_mode: comfortMode,
            channel_1: channel1 === 'on' ? 1 : 0,
            channel_2: channel2 === 'on' ? 1 : 0,
            power: channel1 === 'on' || channel2 === 'on' ? 'on' : 'off',
          }
        : {
            smart_mode: true,
            comfort_mode: comfortMode,
            power: 'on',
          };

    dispatch(
      addPreset({
        id,
        name: name.trim(),
        device_type: deviceType,
        power,
        mode: normalizedMode,
        temp,
        fan,
        color: selectedColor,
        lock,
        ...(!isRelayModel ? { smart_mode: smartMode } : {}),
        ...(isRelayModel ? relayPresetValues : {}),
      }),
    );

    setMsg('Preset saved.');
  };

  return (
    <Paper elevation={0} sx={{ pb: 1, backgroundColor: '#FFFFFF' }}>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={3000}
        vertical="top"
        horizontal="left"
        color="#fff"
      />

      <Stack spacing={2}>
        {/* Header with arrow toggle */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={toggleOpen}
          sx={{
            width: 'fit-content',
            '&:hover': {
              cursor: 'pointer',
            },
            pt: '1rem',
          }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Create a preset{' '}
          </Typography>
          <IconButton
            aria-label={open ? 'Collapse preset form' : 'Expand preset form'}
            aria-expanded={open}
            size="small"
            // onClick={toggleOpen}
            sx={{
              ml: 0.5,
              transition: 'transform 0.2s ease',
            }}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        {/* Everything below collapses */}
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Preset name"
              placeholder="e.g., Office hours"
              value={name}
              sx={{ width: '30%' }}
              onChange={e => setName(e.target.value)}
            />

            <Stack
              className={
                isRelayModel ? 'relay-preset-options-container' : 'preset-options-container'
              }
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {isRelayModel ? (
                  <>
                    <FormControl sx={{ width: 120 }}>
                      <InputLabel id="relay-mode-label">Mode</InputLabel>
                      <Select
                        labelId="relay-mode-label"
                        label="Mode"
                        MenuProps={SELECT_MENU_PROPS}
                        value={relayMode}
                        onChange={e => setMode(e.target.value)}>
                        <MenuItem value="cool">Cool</MenuItem>
                        <MenuItem value="heat">Heat</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl sx={{ width: 140 }}>
                      <InputLabel id="comfort-mode-label">Comfort Mode</InputLabel>
                      <Select
                        labelId="comfort-mode-label"
                        label="Comfort Mode"
                        MenuProps={SELECT_MENU_PROPS}
                        value={comfortMode}
                        onChange={e => setComfortMode(e.target.value)}>
                        <MenuItem value="Cool">Cool</MenuItem>
                        <MenuItem value="Neutral">Neutral</MenuItem>
                        <MenuItem value="Cozy">Cozy</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl sx={{ width: 120 }}>
                      <InputLabel id="smart-mode-label">smart mode</InputLabel>
                      <Select
                        labelId="smart-mode-label"
                        label="smart mode"
                        MenuProps={SELECT_MENU_PROPS}
                        value={controlMode === 'manual' ? 'off' : 'on'}
                        onChange={e => setControlMode(e.target.value === 'on' ? 'solai' : 'manual')}>
                        <MenuItem value="on">ON</MenuItem>
                        <MenuItem value="off">OFF</MenuItem>
                      </Select>
                    </FormControl>

                    {controlMode === 'manual' && (
                      <FormControl sx={{ width: 120 }}>
                        <InputLabel id="relay-power-label">Power</InputLabel>
                        <Select
                          labelId="relay-power-label"
                          label="Power"
                          MenuProps={SELECT_MENU_PROPS}
                          value={relayPower}
                          onChange={e => handleRelayPowerChange(e.target.value)}>
                          <MenuItem value="on">ON</MenuItem>
                          <MenuItem value="off">OFF</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </>
                ) : (
                  <>
                    <FormControl sx={{ width: 120 }}>
                      <InputLabel id="standard-smart-mode-label">Smart mode</InputLabel>
                      <Select
                        labelId="standard-smart-mode-label"
                        label="Smart mode"
                        MenuProps={SELECT_MENU_PROPS}
                        value={smartMode ? 'on' : 'off'}
                        onChange={e => setSmartMode(e.target.value === 'on')}>
                        <MenuItem value="on">ON</MenuItem>
                        <MenuItem value="off">OFF</MenuItem>
                      </Select>
                    </FormControl>

                    {!smartMode && (
                      <FormControl sx={{ width: 120 }}>
                        <InputLabel id="power-label">Power</InputLabel>
                        <Select
                          labelId="power-label"
                          label="Power"
                          MenuProps={SELECT_MENU_PROPS}
                          value={power}
                          onChange={e => setPower(e.target.value)}>
                          <MenuItem value="on">ON</MenuItem>
                          <MenuItem value="off">OFF</MenuItem>
                        </Select>
                      </FormControl>
                    )}

                    <FormControl sx={{ width: 120 }}>
                      <InputLabel id="mode-label">Mode</InputLabel>
                      <Select
                        labelId="mode-label"
                        label="Mode"
                        MenuProps={SELECT_MENU_PROPS}
                        value={mode}
                        onChange={e => setMode(e.target.value)}>
                        <MenuItem value="cool">Cool</MenuItem>
                        <MenuItem value="heat">Heat</MenuItem>
                        <MenuItem value="fan only">Fan only</MenuItem>
                        <MenuItem value="auto">Auto</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl sx={{ width: 120 }}>
                      <InputLabel id="fan-label">Fan speed</InputLabel>
                      <Select
                        labelId="fan-label"
                        label="Fan speed"
                        MenuProps={SELECT_MENU_PROPS}
                        value={fan}
                        onChange={e => setFan(e.target.value)}>
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="med">Med</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="auto">Auto</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl sx={{ width: 120 }}>
                      <InputLabel id="temperature-label">Temperature</InputLabel>
                      <Select
                        labelId="temperature-label"
                        label="Temperature"
                        MenuProps={SELECT_MENU_PROPS}
                        value={temp}
                        onChange={e => setTemp(e.target.value)}>
                        {TEMPS.map(t => (
                          <MenuItem key={t} value={t}>{`${t}°C`}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl sx={{ width: 160 }}>
                      <InputLabel id="lock-label">Lock</InputLabel>
                      <Select
                        labelId="lock-label"
                        label="Lock"
                        MenuProps={SELECT_MENU_PROPS}
                        value={lock}
                        onChange={e => setLock(Number(e.target.value))}>
                        {LOCK_LEVELS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                {/* Color palette */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {PALETTE.map(hex => {
                    const selected = selectedColor === hex;
                    return (
                      <Box
                        key={hex}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedColor(hex)}
                        onKeyDown={e =>
                          (e.key === 'Enter' || e.key === ' ') && setSelectedColor(hex)
                        }
                        aria-label={`Select color ${hex}`}
                        sx={{
                          width: 25,
                          height: 25,
                          borderRadius: '6px',
                          backgroundColor: hex,
                          border: selected ? `2px solid black` : '2px solid #E0E0E0',
                          outline: 'none',
                          cursor: 'pointer',
                        }}
                      />
                    );
                  })}
                </Box>
              </Stack>

              <Button
                variant="inherit"
                onClick={handleSubmit}
                sx={{
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: '.9rem',
                  color: '#00000095',
                  border: '2px solid #00000075',
                }}>
                <SaveIcon sx={{ scale: '1.2', marginRight: '.5rem' }}></SaveIcon>SAVE PRESET
              </Button>
            </Stack>

            {msg && (
              <Typography
                variant="caption"
                sx={{ color: '#414141ff', fontSize: '1rem', fontWeight: '500' }}>
                {msg}
              </Typography>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
